import { createClient } from '@epilot/sdk/phone-integration'
import type { Client, ErrorResponse } from '@epilot/sdk/phone-integration'

import { configureClient } from './api-utils'

import { config } from '@/configs/api.config'

let client: Client | undefined

const fallbackMessage = (status?: number) =>
  status === 409
    ? 'The telephony provider refused the setup (for example, the account name is already taken).'
    : status === 502
    ? 'The telephony provider could not be reached or answered unexpectedly. Try again in a moment.'
    : status === 403
    ? 'Phone setup requires the admin or super-admin group in this stage’s admin portal.'
    : status === 401
    ? 'Your admin session expired. Sign in again.'
    : status === 400
    ? 'The API rejected the setup request. Check the account name, application and secret.'
    : 'The phone integration request failed. Refresh to check the latest state before retrying.'

/**
 * phone-integration-api answers every refusal with `{ status, error }` written for the operator.
 * Surface it and fall back to a status-based text only when the body carries none. The Axios error
 * itself is never propagated: `setupProvider` carries the identity client secret in its request
 * body, and an Axios error keeps the request config (body and bearer) where any logger could print it.
 */
const errorMessage = (error: unknown) => {
  const response = (
    error as { response?: { status?: number; data?: unknown } } | null
  )?.response
  // The API's ErrorResponse, or API Gateway's own `{ message }` (e.g. an authorizer 403); read
  // defensively, the body is untrusted.
  const body = response?.data as
    | (Partial<ErrorResponse> & { message?: unknown })
    | undefined
  const detail = [body?.error, body?.message].find(
    (value): value is string => typeof value === 'string' && value.trim() !== ''
  )

  return detail ? detail.trim() : fallbackMessage(response?.status)
}

export const getPhoneIntegrationClient = (): Client => {
  if (!config.PHONE_INTEGRATION)
    throw new Error('Phone integration API is not configured for this stage.')
  if (!client) {
    client = configureClient(createClient(), {
      baseURL: config.PHONE_INTEGRATION
    })
    // the setup run makes several sequential provider calls behind a 30 s Lambda
    client.defaults.timeout = 35000
    client.interceptors.response.use(
      (response) => response,
      (error: unknown) => Promise.reject(new Error(errorMessage(error)))
    )
  }

  return client
}
